import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Loader2 } from "lucide-react";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import CookieConsent from "@/components/CookieConsent";
import InstallPrompt from "@/components/InstallPrompt";

const Confidentialite = lazy(() => import("@/pages/Confidentialite"));
const LegalDoc = lazy(() => import("@/pages/LegalDoc"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Aujourdhui = lazy(() => import("@/pages/Aujourdhui"));
const ResourcePage = lazy(() => import("@/pages/ResourcePage"));
const Pipeline = lazy(() => import("@/pages/Pipeline"));
const Priorites = lazy(() => import("@/pages/Priorites"));
const Calendrier = lazy(() => import("@/pages/Calendrier"));
const Documents = lazy(() => import("@/pages/Documents"));
const Rapports = lazy(() => import("@/pages/Rapports"));
const Relances = lazy(() => import("@/pages/Relances"));
const Automatisations = lazy(() => import("@/pages/Automatisations"));
const Sauvegardes = lazy(() => import("@/pages/Sauvegardes"));
const Abonnements = lazy(() => import("@/pages/Abonnements"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const CloudConnect = lazy(() => import("@/pages/CloudConnect"));
const AssistantIA = lazy(() => import("@/pages/AssistantIA"));
const Objectifs = lazy(() => import("@/pages/Objectifs"));
const Modeles = lazy(() => import("@/pages/Modeles"));
const SanteAnalyses = lazy(() => import("@/pages/SanteAnalyses"));
const ClientDetail = lazy(() => import("@/pages/ClientDetail"));
const ModeChantier = lazy(() => import("@/pages/ModeChantier"));
const Audit = lazy(() => import("@/pages/Audit"));
const Signatures = lazy(() => import("@/pages/Signatures"));
const Rentabilite = lazy(() => import("@/pages/Rentabilite"));
const Parametres = lazy(() => import("@/pages/Parametres"));
const Securite = lazy(() => import("@/pages/Securite"));
const Conformite = lazy(() => import("@/pages/Conformite"));
const CentreMaitre = lazy(() => import("@/pages/CentreMaitre"));
const CentreCreateur = lazy(() => import("@/pages/CentreCreateur"));
const CentreTechnicien = lazy(() => import("@/pages/CentreTechnicien"));
const Interne = lazy(() => import("@/pages/Interne"));
const ActivateOwner = lazy(() => import("@/pages/ActivateOwner"));
const Nouveautes = lazy(() => import("@/pages/Nouveautes"));
const PortailLogin = lazy(() => import("@/pages/PortailLogin"));
const PortailVerify = lazy(() => import("@/pages/PortailVerify"));
const PortailEspace = lazy(() => import("@/pages/PortailEspace"));
const SsoConsume = lazy(() => import("@/pages/SsoConsume"));
import { MODULES } from "@/config/modules";
import { LanguageProvider } from "@/i18n";

function Loading() {
  return <div className="min-h-screen flex items-center justify-center bg-[#07080A]"><Loader2 className="animate-spin text-[#F2BC2E]" size={28} /></div>;
}

function Protected({ children, allowNoSub, requireAdmin }) {
  const { user, loading, needsSubscription } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (requireAdmin && !user.is_platform_admin) return <Navigate to="/aujourdhui" replace />;
  if (needsSubscription && !allowNoSub) return <Navigate to="/abonnements" replace />;
  return <Layout>{children}</Layout>;
}

function RootRoute() {
  const { user, loading, needsSubscription } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Landing />;
  if (needsSubscription) return <Navigate to="/abonnements" replace />;
  return <Layout><Dashboard /></Layout>;
}

const R = (config) => <Protected><ResourcePage config={config} /></Protected>;

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
        <AuthProvider>
          <Toaster theme="dark" position="top-right" richColors />
          <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<RootRoute />} />
            <Route path="/login" element={<Login />} />
            <Route path="/sso/consume" element={<SsoConsume />} />
            <Route path="/interne" element={<Interne />} />
            <Route path="/activate-owner" element={<ActivateOwner />} />
            <Route path="/centre-createur" element={<CentreCreateur />} />
            <Route path="/centre-technicien" element={<CentreTechnicien />} />
            <Route path="/portail" element={<PortailLogin />} />
            <Route path="/portail/verifier" element={<PortailVerify />} />
            <Route path="/portail/espace" element={<PortailEspace />} />
            <Route path="/confidentialite" element={<Confidentialite />} />
            <Route path="/conditions" element={<LegalDoc docType="cgu" />} />
            <Route path="/temoins" element={<LegalDoc docType="temoins" />} />
            <Route path="/responsabilite" element={<LegalDoc docType="responsabilite" />} />
            <Route path="/aujourdhui" element={<Protected><Aujourdhui /></Protected>} />
            <Route path="/sante" element={<Protected><SanteAnalyses /></Protected>} />
            <Route path="/rentabilite" element={<Protected><Rentabilite /></Protected>} />
            <Route path="/objectifs" element={<Protected><Objectifs /></Protected>} />
            <Route path="/prospects" element={R(MODULES.prospects)} />
            <Route path="/priorites" element={<Protected><Priorites /></Protected>} />
            <Route path="/clients" element={R(MODULES.clients)} />
            <Route path="/client/:id" element={<Protected><ClientDetail /></Protected>} />
            <Route path="/contacts" element={R(MODULES.contacts)} />
            <Route path="/opportunites" element={R(MODULES.opportunites)} />
            <Route path="/pipeline" element={<Protected><Pipeline /></Protected>} />
            <Route path="/soumissions" element={R(MODULES.soumissions)} />
            <Route path="/signatures" element={<Protected><Signatures /></Protected>} />
            <Route path="/taches" element={R(MODULES.taches)} />
            <Route path="/relances" element={<Protected><Relances /></Protected>} />
            <Route path="/automatisations" element={<Protected><Automatisations /></Protected>} />
            <Route path="/sauvegardes" element={<Protected><Sauvegardes /></Protected>} />
            <Route path="/calendrier" element={<Protected><Calendrier /></Protected>} />
            <Route path="/communications" element={R(MODULES.communications)} />
            <Route path="/modeles" element={<Protected><Modeles /></Protected>} />
            <Route path="/documents" element={<Protected><Documents /></Protected>} />
            <Route path="/rapports" element={<Protected><Rapports /></Protected>} />
            <Route path="/assistant-ia" element={<Protected><AssistantIA /></Protected>} />
            <Route path="/mode-chantier" element={<Protected><ModeChantier /></Protected>} />
            <Route path="/support" element={R(MODULES.tickets)} />
            <Route path="/utilisateurs" element={R(MODULES.utilisateurs)} />
            <Route path="/audit" element={<Protected><Audit /></Protected>} />
            <Route path="/abonnements" element={<Protected allowNoSub><Abonnements /></Protected>} />
            <Route path="/integrations" element={<Protected><Integrations /></Protected>} />
            <Route path="/cloud-connect" element={<Protected><CloudConnect /></Protected>} />
            <Route path="/parametres" element={<Protected><Parametres /></Protected>} />
            <Route path="/securite" element={<Protected><Securite /></Protected>} />
            <Route path="/conformite" element={<Protected><Conformite /></Protected>} />
            <Route path="/nouveautes" element={<Protected allowNoSub><Nouveautes /></Protected>} />
            <Route path="/centre-maitre" element={<Protected allowNoSub requireAdmin><CentreMaitre /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          <CookieConsent />
          <InstallPrompt />
        </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
